import { Box, Typography, Stack } from '@mui/material';
import { debounce } from '@mui/material/utils';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ECElementEvent } from 'echarts';
import { buildGaugeOptions } from '../chartWidgetUtility';
import { useTheme } from '@mui/material/styles';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';

interface GuageChartProps {
  isLoading: boolean;
  title?: string;
  value?: number | null;
  tooltipLabel?:
    | string
    | JSX.Element
    | {
        title: string;
        description: string | React.ReactNode;
      }[];
  hasTooltip?: boolean;
  sectorLabel?: string;
  handleEvent?: (event: ECElementEvent, title?: string) => void;
  chartSize?: string;
}

const GaugeChart = ({
  isLoading = false,
  title = '',
  value = null,
  hasTooltip = false,
  tooltipLabel = '',
  sectorLabel = '',
  handleEvent,
  chartSize,
}: Readonly<GuageChartProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartRef = useRef<any>(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = debounce(() => setWindowSize(window.innerWidth), 200);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const defaultOptions = {
    backgroundColor: colors.widget.background.card,
    series: [
      {
        type: 'gauge',
        center: ['50%', '45%'],
        min: 0,
        max: 0,
        splitNumber: 5,
        itemStyle: { color: '#e5e5e5' },
        progress: { show: false },
        pointer: { show: false },
        emphasis: { disabled: true },
      },
    ],
  };

  const adjustedOption = useMemo(() => {
    if (typeof value !== 'number') return defaultOptions;
    return buildGaugeOptions(value, colors);
  }, [value, defaultOptions]);

  return (
    <Box display="flex" flexGrow={1} height="100%" position="relative">
      <Stack
        minHeight="100px"
        p={2}
        borderRadius="5px"
        flexGrow={1}
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
          color: `${colors.widget.text.main} !important`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="start">
          <Typography
            variant="widgetTitle"
            sx={{ color: `${colors.widget.text.title} !important` }}
          >
            <HighlightedText text={title} />
          </Typography>

          {sectorLabel && (
            <Box
              sx={{
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                padding: '2px 8px',
                backgroundColor: '#e4e4e4',
              }}
            >
              <Typography variant="bodyText">{sectorLabel}</Typography>
            </Box>
          )}

          {hasTooltip && !!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
        </Stack>

        <Stack flexGrow={1} justifyContent="center" key={windowSize} ref={chartRef} minHeight={chartSize ? chartSize : '500px'}>
          {!isLoading ? (
            <ReactECharts
              style={{ width: '100%', height: '100%' }}
              option={adjustedOption}
              notMerge
              lazyUpdate
              onEvents={{
                click: (params: ECElementEvent) => {
                  if (handleEvent) handleEvent(params, title);
                },
              }}
            />
          ) : (
            <LoaderComponent />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default GaugeChart;
